import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactTimeout from 'react-timeout';
import Hls from 'hls.js';
import _ from 'lodash';
import { Link } from 'react-router';
import classNames from 'classnames';
import VisibilitySensor from 'react-visibility-sensor';
import Icon from 'components/Icon';
import { heroCarouselEnabled, immersiveShowcaseEnabled } from 'helpers/ConfigHelper';
import Style from './VideoHeader.module.scss';

const TRANSITION_TIME_IN = 500;

class VideoHeader extends Component {
  constructor(props) {
    super(props);
    const { id, idx } = props;
    this.state = {
      videoElement: null,
      hlsObjects: [this.createNewHls()],
      initialized: false,
      loaded: false,
      isVisible: false,
      // use undefined rather than adding empty string on the dom
      videoElementID: idx === 0 ? this.createUniqueId(id, idx) : undefined,
    };
  }

  componentDidMount() {
    const { video, play, onMount, transitionImage, setHeaderImageState } = this.props;
    const { videoElementID } = this.state;
    if (onMount) onMount();
    window.addEventListener('scroll', this.handlePauseOnScroll);
    if (video) {
      setHeaderImageState('teaser video');
      return this.loadVideo({ play, video });
    }
    setHeaderImageState('static image');
    if (transitionImage && videoElementID) {
      const videoElement = this.getVideoElement();
      return this.setState({ videoElement }, this.transitionImageStart);
    }
    return null;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { play, video, image, setTimeout } = this.props;
    const { play: nextPlay, video: nextVideo, image: nextImage } = nextProps;
    const { videoElement, loaded, initialized, isVisible } = this.state;
    // Load video right away if it's the first one
    if (!initialized && nextVideo && isVisible) {
      this.loadVideo({ play: nextPlay, video: nextVideo });
      this.setState({ initialized: true });
      return;
    }

    // Start hero transition if the source is changing
    if (video !== nextVideo || image !== nextImage) {
      if (nextVideo) {
        // If transitioning to a video, load video before completing transition
        setTimeout(this.transitionStart.bind(this), TRANSITION_TIME_IN);
      } else {
        // Otherwise, unload the video and start the end transition
        setTimeout(this.transitionImageStart.bind(this), TRANSITION_TIME_IN);
      }
    } else if (play !== nextPlay && loaded) {
      if (!nextPlay) {
        videoElement.pause();
        this.handleCallback('paused');
      } else {
        this.playVideoSafely(videoElement);
        this.handleCallback('playing');
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { play: nextPlay, video: nextVideo } = this.props;
    const { isVisible, initialized } = this.state;
    const { isVisible: prevIsVisible } = prevState;
    if (!initialized && isVisible && !prevIsVisible) {
      this.loadVideo({ play: nextPlay, video: nextVideo });
      this.setState({ initialized: true });
    }
    if (!isVisible && prevIsVisible) {
      this.transitionImageStart();
    }
  }

  componentWillUnmount() {
    const { onUnmount } = this.props;
    window.removeEventListener('scroll', this.handlePauseOnScroll);
    if (onUnmount) onUnmount();
  }

  transitionImageStart() {
    const { video, image } = this.props;
    const { hlsObjects, videoElementID } = this.state;
    if (!videoElementID) return;
    const hlsObject = this.getHlsObject();
    hlsObject.destroy();
    const videoElement = this.getVideoElement();
    const newVideoImage = hlsObject && video ? null : image;
    const newHlsObject = this.createNewHls();
    videoElement.src = '';
    this.loadPoster({ poster: newVideoImage });
    videoElement.load();

    const newHlsObjects = _.concat(hlsObjects, newHlsObject);
    this.setState({ videoElement, hlsObjects: newHlsObjects });
  }

  transitionStart() {
    const { video } = this.props;
    const { videoElement, hlsObjects } = this.state;
    const hlsObject = this.getHlsObject();
    hlsObject.destroy();
    const newHlsObject = this.createNewHls();
    if (!newHlsObject) return;
    if (videoElement) {
      videoElement.setAttribute('src', video);
      videoElement.setAttribute('style', null);
    }
    newHlsObject.loadSource(video);
    newHlsObject.attachMedia(videoElement);

    const newHlsObjects = _.concat(hlsObjects, newHlsObject);
    this.setState({ videoElement, hlsObjects: newHlsObjects });
  }

  loadNewVideoElement() {
    const { videoElement: oldVideoElement, videoElementID } = this.state;
    if (!videoElementID) return;
    const videoElement = this.getVideoElement();
    if (!oldVideoElement) this.setState({ videoElement });
  }

  getVideoElement() {
    const { id, idx } = this.props;
    const videoId = this.createUniqueId(id, idx);
    const videoElement = document.getElementById(videoId);
    if (videoElement) {
      videoElement.addEventListener('error', this.videoTagListener, false);
      videoElement.addEventListener('loadeddata', this.videoTagListener, false);
    }
    return videoElement;
  }

  getHlsObject() {
    const { hlsObjects } = this.state;
    return _.last(hlsObjects);
  }

  loadVideo({ video }) {
    const { videoElement: oldVideoElement, videoElementID } = this.state;
    if (!video || !videoElementID) return;
    const hlsObject = this.getHlsObject();
    const videoElement = this.getVideoElement();
    videoElement.setAttribute('src', video);
    hlsObject.loadSource(video);
    hlsObject.attachMedia(videoElement);

    if (!oldVideoElement) this.setState({ videoElement });
  }

  createNewHls() {
    const config = {
      maxBufferHole: 0,
      maxLoadingDelay: 1000,
    };
    const newHlsObject = new Hls(config);
    let listenerAttachedVideo;
    let listenerParsedVideo;
    if (this.handleParsedVideo) {
      listenerParsedVideo = e => this.handleParsedVideo(e);
    }
    if (this.listenerAttachedVideo) {
      listenerAttachedVideo = e => this.listenerAttachedVideo(e);
    }
    if (newHlsObject) {
      if (listenerParsedVideo) newHlsObject.on(Hls.Events.MANIFEST_PARSED, listenerParsedVideo);
      if (listenerAttachedVideo) newHlsObject.on(Hls.Events.MEDIA_ATTACHED, listenerAttachedVideo);
    }
    return newHlsObject;
  }

  videoTagListener = event => {
    if (!event) return;

    switch (event.type) {
      case 'loadeddata':
        this.handleParsedVideo(event);
        if (this.listenerAttachedVideo) this.listenerAttachedVideo(event);
        break;
      case 'error':
        break;
      default:
      // do nothing
    }
  };

  handleCallback(...args) {
    const { callback } = this.props;
    const cb = callback || null;
    if (cb && typeof cb === 'function') {
      cb.apply(cb, args);
    }
  }

  handlePauseOnScroll = () => {
    const { videoElementID } = this.state;
    if (!videoElementID) return;
    const videoElement = this.getVideoElement();
    const videoElementHeight = videoElement.clientHeight;
    if (window.scrollY >= videoElementHeight) {
      videoElement.pause();
    } else {
      this.playVideoSafely(videoElement);
    }
  };

  playVideoSafely(videoElement) {
    const video = videoElement || this.getVideoElement();
    let playPromise;
    if (video) {
      playPromise = video.play();
    }
    if (playPromise) {
      playPromise.catch(() => {
        // prevents the video from throwing an error.
        console.debug('Error playing video header element');
      });
    }
  }

  handleParsedVideo() {
    const { play, timer: currentTimer, setTimeout } = this.props;
    let timer = parseInt(currentTimer, 10);
    timer = Number.isNaN(timer) ? 0 : timer * 1000;

    if (play) {
      setTimeout(() => {
        const { play: playNow } = this.props;
        if (playNow) {
          this.playVideoSafely();
          this.handleCallback('playing');
        }
      }, timer);
    } else {
      this.handleCallback('loaded');
    }
  }

  createUniqueId = (id, idx) => `${idx}-${id}`;

  renderSingleVideoHeader() {
    const {
      id,
      idx,
      video,
      image,
      play,
      themeSingleVideoHeader,
      promoTypeIsShowcaseBurntIn,
    } = this.props;
    const { videoElementID } = this.state;
    const networksBar = window.ExternalConfig.networkNavigation;
    const containerClasses = classNames(
      themeSingleVideoHeader.container,
      networksBar ? themeSingleVideoHeader.withNetworksBar : '',
      heroCarouselEnabled() ? themeSingleVideoHeader.carouselWrappedContainer : '',
    );
    const styles = {
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      overflow: 'hidden',
    };

    const videoProps = {
      id: videoElementID,
      muted: 'muted',
      playsInline: 'playsInline',
      loop: 'loop',
    };

    if (video && play) {
      videoProps.autoPlay = 'autoPlay';
    }

    const videoHeaderClassNames = classNames(
      containerClasses,
      themeSingleVideoHeader.redesignCollection,
      promoTypeIsShowcaseBurntIn ? themeSingleVideoHeader.heroPlayerCTANationBurntInImage : null,
    );
    return (
      <VisibilitySensor
        onChange={isVisible => {
          // use undefined rather than adding empty string on the dom
          const videoElementID = isVisible ? this.createUniqueId(id, idx) : undefined;
          this.setState({ isVisible, videoElementID });
        }}
      >
        <div className={videoHeaderClassNames}>
          <div className={classNames(themeSingleVideoHeader.shadowOverlay)} />
          <div className={themeSingleVideoHeader.videoContainer}>
            <video
              style={{ ...styles }}
              poster={image}
              {...videoProps}
              className={themeSingleVideoHeader.video}
            />
          </div>
          {immersiveShowcaseEnabled() && <div className={themeSingleVideoHeader.additionalFader} />}
        </div>
      </VisibilitySensor>
    );
  }

  renderVideoHeaders() {
    const {
      id,
      idx,
      video,
      image,
      play,
      handleTitleClick,
      watchLink,
      showPlayButton,
      shadowOnPlayButton,
    } = this.props;

    const setDimensions = {
      width: '1200px',
      height: '675px',
    };

    const styles = {
      ...setDimensions,
      backgroundImage: `url('${image}')`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    };

    const videoProps = {
      id: this.createUniqueId(id, idx),
      muted: 'muted',
      playsInline: 'playsInline',
      loop: 'loop',
    };

    if (video && play) {
      videoProps.autoPlay = 'autoPlay';
    }

    return (
      <div className={Style.overlay}>
        <Link onClick={handleTitleClick} to={watchLink} className={Style.link} />
        <div
          className={classNames(Style.playButton, shadowOnPlayButton ? Style.playButtonShadow : '')}
        >
          <div>{showPlayButton && <Icon icon="play-button" color="white" size="full" />}</div>
        </div>
        {video ? <video style={{ ...styles }} {...videoProps} /> : <div style={{ ...styles }} />}
      </div>
    );
  }

  render() {
    const { foxAwesomeLayout } = this.props;
    return (
      <div>{foxAwesomeLayout ? this.renderSingleVideoHeader() : this.renderVideoHeaders()}</div>
    );
  }
}

VideoHeader.defaultProps = {
  callback: () => {},
  foxAwesomeLayout: false,
  handleTitleClick: null,
  id: '',
  image: null,
  promoTypeIsShowcaseBurntIn: false,
  setHeaderImageState: () => {},
  setTimeout: () => {},
  shadowOnPlayButton: false,
  showPlayButton: true,
  themeSingleVideoHeader: null,
  timer: null,
  transitionImage: null,
  video: null,
  watchLink: null,
};

VideoHeader.propTypes = {
  callback: PropTypes.func,
  foxAwesomeLayout: PropTypes.bool,
  handleTitleClick: PropTypes.func,
  id: PropTypes.string,
  idx: PropTypes.number.isRequired,
  image: PropTypes.string,
  onMount: PropTypes.func.isRequired,
  onUnmount: PropTypes.func.isRequired,
  play: PropTypes.bool.isRequired,
  promoTypeIsShowcaseBurntIn: PropTypes.bool,
  setHeaderImageState: PropTypes.func,
  setTimeout: PropTypes.func,
  shadowOnPlayButton: PropTypes.bool,
  showPlayButton: PropTypes.bool,
  themeSingleVideoHeader: PropTypes.shape({}),
  timer: PropTypes.number,
  transitionImage: PropTypes.string,
  video: PropTypes.string,
  watchLink: PropTypes.string,
};

export default ReactTimeout(VideoHeader);
